import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { PhoneNumberFormatPipe } from 'src/app/libs/ui/pipes/phone-number-format/phone-number-format.pipe';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import {
  onEnterBackSpace,
  transformPhone,
} from 'src/app/shared/utils/utility.service';
import {
  IUser,
  IUserProfile,
} from 'src/app/user-management/user-management-interfaces';
import { UserManagementService } from 'src/app/user-management/user-management.service';
import { StateManagementService } from 'src/app/shared/services/state-management.service';
import { TranslateService } from '@ngx-translate/core';
import { ERROR_CODES } from 'src/app/shared/constants/error-codes';

@Component({
  selector: 'app-edit-phone-number',
  templateUrl: './edit-phone-number.component.html',
  styleUrls: ['./edit-phone-number.component.scss'],
})
export class EditPhoneNumberComponent implements OnInit {
  @Input() stepper!: number;
  @Output() stepperChange = new EventEmitter<number>();
  @Input() dialogRef?: any;
  @Input() profileDetails!: IUserProfile;
  @Input() siteId!: string;
  @ViewChild('phoneInputRef') phoneInputRef: any;

  editPhoneForm!: FormGroup;
  regionValue: string = CONSTANTS.PHONE_NUMBER_REGION;
  maxLength = 14;
  isSubmitting = false;
  loginUser!: IUser;

  /**
   * @description only a Property Manager Admin (PMA) or Property Manager User (PMU)
   *              is allowed to edit a pending user's phone number; mirrors the backend
   *              authorization that returns USER_NOT_AUTHORIZED for any other role.
   */
  get isAuthorized(): boolean {
    return (
      this.loginUser?.userType === CONSTANTS.PROPERTY_MANAGER_ADMIN ||
      this.loginUser?.userType === CONSTANTS.PROPERTY_MANAGER_USER
    );
  }

  get formInstance() {
    return this.editPhoneForm.controls;
  }

  constructor(
    private fb: FormBuilder,
    private helperService: HelperService,
    private phoneNumberFormat: PhoneNumberFormatPipe,
    private notificationService: NotificationService,
    private localizationService: LocalizationService,
    private userManagementService: UserManagementService,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.localizationService.setupLocalization();
    this.loginUser = this.stateManagementService.getUser();
    this.editPhoneForm = this.fb.group({
      region: [
        this.regionValue,
        MFValidators.compose([MFValidators.required, MFValidators.region]),
      ],
      phone: [
        '',
        MFValidators.compose([MFValidators.required, MFValidators.phone]),
      ],
    });
    if (this.profileDetails?.phoneNumber) {
      this.setPhoneNumber(this.profileDetails.phoneNumber);
    }
  }

  setPhoneNumber(phone: string): void {
    this.helperService.validatePhoneFormat(phone).subscribe(
      ({
        phoneNumber,
        region,
        code,
      }: {
        phoneNumber: string;
        region: string;
        code: string;
      }) => {
        this.regionValue = code;
        this.editPhoneForm.patchValue({
          region: code,
          phone: this.phoneNumberFormat.transform(phoneNumber, code),
        });
      },
      (err: string) => {
        if (window.__dev__) console.log('validatePhoneFormat', err);
      }
    );
  }

  onInputBackSpace(ev: any): void {
    onEnterBackSpace(ev, this.phoneInputRef);
  }

  onRegionSelected(region: any): void {
    this.maxLength = this.helperService.phoneNumberLength(region);
  }

  onUpdate(): void {
    if (this.editPhoneForm.invalid || this.isSubmitting) {
      return;
    }
    // guard on the frontend too: backend allows only PMA/PMU (USER_NOT_AUTHORIZED otherwise)
    if (!this.isAuthorized) {
      this.notificationService.showError(
        this.translate.instant('YOU_ARE_NOT_AUTHORIZED_TO_MAKE_THIS_CALL')
      );
      return;
    }
    const phoneNumber = transformPhone(
      `${this.formInstance.region.value}${this.formInstance.phone.value}`
    );
    this.isSubmitting = true;
    this.userManagementService
      .updatePendingUserPhoneNumber(
        this.siteId,
        this.profileDetails?.email!,
        phoneNumber
      )
      .subscribe({
        next: (success: boolean) => {
          this.isSubmitting = false;
          if (success) {
            this.stepperChange.emit((this.stepper = 16));
          }
        },
        error: (err: Error) => {
          this.isSubmitting = false;
          if (window.__dev__) console.log('updatePendingUserPhoneNumber', err);
          this.notificationService.showError(
            this.translate.instant(this.mapPhoneError(err?.message))
          );
        },
      });
  }

  /**
   * @description maps the backend errorType for a phone update to a user-friendly,
   *              translatable message key.
   * @param { string } errorType raw errorType/message thrown by the service
   * @returns { string } translation key to display
   */
  private mapPhoneError(errorType: string): string {
    switch (errorType) {
      case ERROR_CODES.USER_NOT_AUTHORIZED:
        return 'YOU_ARE_NOT_AUTHORIZED_TO_MAKE_THIS_CALL';
      case ERROR_CODES.USER_NOT_FOUND:
        return 'USER_NOT_FOUND';
      case ERROR_CODES.USER_ALREADY_SIGNED_UP:
        return 'USER_ALREADY_SIGNED_UP';
      case ERROR_CODES.PHONE_NUMBER_ALREADY_EXISTS:
        return 'PHONE_NUMBER_ALREADY_EXISTS';
      default:
        return 'FAILED_TO_UPDATE_PHONE_NUMBER_TRY_AGAIN';
    }
  }

  goBack(): void {
    this.stepperChange.emit((this.stepper = 1));
  }

  closeDialog(): void {
    this.dialogRef?.close();
  }
}
